import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

import en from '../../src/context/locales/en.json';
import bn from '../../src/context/locales/bn.json';
import hn from '../../src/context/locales/hn.json';

export const translations = { EN: en, BN: bn, HN: hn };

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    console.warn('useLanguage must be used within a LanguageProvider - using default English');
    return {
      language: 'EN',
      showLanguageMenu: false,
      toggleLanguageMenu: () => {},
      closeLanguageMenu: () => {},
      changeLanguage: () => {},
      t: translations.EN
    };
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem("preferredLanguage");
    return savedLanguage && ["EN", "BN", "HN"].includes(savedLanguage)
      ? savedLanguage
      : "EN";
  });
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  useEffect(() => {
    localStorage.setItem("preferredLanguage", language);
    console.log("Language changed to:", language);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
  }, [language]);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      if (event.detail && event.detail.language) {
        console.log("Language changed from another tab:", event.detail.language);
        setLanguage(event.detail.language);
      }
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const changeLanguage = useCallback((lang) => {
    console.log("Changing language to:", lang);
    if (["EN", "BN", "HN"].includes(lang)) {
      setLanguage(lang);
      setShowLanguageMenu(false);
    }
  }, []);

  const toggleLanguageMenu = useCallback(() => {
    setShowLanguageMenu(prev => !prev);
  }, []);

  const closeLanguageMenu = useCallback(() => {
    setShowLanguageMenu(false);
  }, []);

  const t = translations[language] || translations.EN;

  const value = {
    language,
    setLanguage,
    showLanguageMenu,
    setShowLanguageMenu,
    changeLanguage,
    toggleLanguageMenu,
    closeLanguageMenu,
    t,
    translations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;